import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDeleteEmailModalComponent } from './confirm-delete-email-modal/confirm-delete-email-modal.component';
import { EmailNotificationSettingsService } from './email-notification-settings.service';
import { EmailNotificationSettings } from './email-notification-settings.model';

interface EmailEntry {
  value: string;
  error: string;
  isEditing?: boolean;
  originalValue?: string;
}

@Component({
  selector: 'app-email-notification-settings',
  templateUrl: './email-notification-settings.component.html',
  styleUrls: ['./email-notification-settings.component.scss']
})
export class EmailNotificationSettingsComponent implements OnInit {
  applicationId = '';
  emailNotificationForm: FormGroup;
  validationMessage = '';
  emails: EmailEntry[] = [];
  emailInput = '';
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  constructor(private fb: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog,
              private emailNotificationSettingsService: EmailNotificationSettingsService) {
    this.emailNotificationForm = this.fb.group({
      notificationType: ['off'],
      positiveVerification: [false],
      negativeVerification: [false]
    });
  }

  ngOnInit(): void {
    this.applicationId = this.route.snapshot.params.id;
    this.populateForm(this.route.snapshot.data.stepData);
    const notificationControl = this.emailNotificationForm.get('notificationType');
    if (!this.anyVerificationSelected()) {
      notificationControl?.disable();
    } else {
      notificationControl?.enable();
    }
  }

  save() {
    if (!this.validateForm()) {
      return;
    }

    const formValue = this.emailNotificationForm.getRawValue();
    const payload: EmailNotificationSettings = {
      id: this.applicationId,
      emails: this.emails.map(entry => entry.value),
      notificationFrequency: this.getNormalizedNotificationType().toUpperCase(),
      notificationsToMonitor: {
        positive: formValue.positiveVerification,
        negative: formValue.negativeVerification
      }
    };

    this.emailNotificationSettingsService.updateEmailNotificationSettingsData(this.applicationId, payload)
      .subscribe(
        () => this.router.navigateByUrl(`/admin/applicationsSetup/7/${this.applicationId}`),
        error => this.validationMessage = this.getApiErrorMessage(error)
      );
  }

  back() {
    this.router.navigateByUrl(`/admin/applicationsSetup/5/${this.applicationId}`);
  }

  addEmail() {
    const trimmed = this.emailInput.trim();
    if (!trimmed || this.emails.length >= 3 || !this.isValidEmail(trimmed) || this.isDuplicateEmail(trimmed)) {
      return;
    }

    this.emails.push({ value: trimmed, error: '', isEditing: false });
    this.emailInput = '';
  }

  deleteEmail(index: number) {
    const dialogRef = this.dialog.open(ConfirmDeleteEmailModalComponent, {
      width: '420px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.emails.splice(index, 1);
      }
    });
  }

  editEmail(index: number) {
    const editedIndex = this.emails.findIndex(entry => entry.isEditing);
    if (editedIndex !== -1 && editedIndex !== index && !this.validateEmailEdit(editedIndex)) {
      return;
    }

    this.emails.forEach((entry, idx) => {
      entry.isEditing = idx === index;
      if (idx === index) {
        entry.originalValue = entry.value;
      }
    });
  }

  saveEmailEdit(index: number) {
    this.validateEmailEdit(index);
  }

  cancelEmailEdit(index: number) {
    const entry = this.emails[index];
    if (!entry) {
      return;
    }

    entry.value = entry.originalValue || entry.value;
    entry.error = '';
    entry.isEditing = false;
    delete entry.originalValue;
  }

  canAddEmail(): boolean {
    const trimmed = this.emailInput.trim();
    return !!trimmed && this.emails.length < 3 && this.isValidEmail(trimmed) && !this.isDuplicateEmail(trimmed);
  }

  getAddEmailHintMessage(): string {
    const trimmed = this.emailInput.trim();
    if (!trimmed) {
      return '';
    }

    if (this.isDuplicateEmail(trimmed)) {
      return 'Duplicate email addresses are not allowed.';
    }

    if (!this.isValidEmail(trimmed)) {
      return 'Enter a valid Roche or Genentech email address.';
    }

    return '';
  }

  isValidEmail(value: string): boolean {
    return !this.getEmailValidationError(value);
  }

  selectNotificationType(type: string) {
    this.emailNotificationForm.patchValue({ notificationType: type });
  }

  isNotificationTypeSelected(type: string): boolean {
    return this.emailNotificationForm.value.notificationType === type;
  }

  toggleVerificationFlags() {
    const values = this.emailNotificationForm.value;
    const notificationTypeControl = this.emailNotificationForm.get('notificationType');

    if (!values.positiveVerification && !values.negativeVerification) {
      this.emailNotificationForm.patchValue({ notificationType: 'off' });
      notificationTypeControl?.disable();
    } else {
      if (notificationTypeControl?.disabled) {
        notificationTypeControl.enable();
      }
      // Do not auto-select another notificationType; keep 'off' by default
    }
  }

  anyVerificationSelected(): boolean {
    const v = this.emailNotificationForm.value;
    return !!(v.positiveVerification || v.negativeVerification);
  }

  validateForm(): boolean {
    this.validationMessage = '';
    this.emails.forEach(email => email.error = '');

    const invalidEmails = this.emails.filter(entry => {
      const trimmed = entry.value.trim();
      entry.error = this.getEmailValidationError(trimmed);
      return !!entry.error;
    });

    if (invalidEmails.length) {
      this.validationMessage = 'Enter a valid Roche or Genentech email address.';
      return false;
    }

    if (this.emails.some((entry, index) => this.isDuplicateEmail(entry.value, index))) {
      this.validationMessage = 'Duplicate email addresses are not allowed.';
      return false;
    }

    const notificationType = this.getNormalizedNotificationType();

    if (notificationType === 'off') {
      return true;
    }

    if (!this.emails.length) {
      this.validationMessage = 'At least one email address is required when notifications are enabled.';
      return false;
    }

    return true;
  }

  getNormalizedNotificationType(): string {
    const values = this.emailNotificationForm.getRawValue();
    if (!values.positiveVerification && !values.negativeVerification) {
      return 'off';
    }

    return values.notificationType || 'off';
  }

  private getEmailValidationError(value: string): string {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed || !this.emailPattern.test(trimmed)) {
      return 'Enter a valid Roche or Genentech email address.';
    }

    const domain = trimmed.split('@')[1];
    const isRoche = domain === 'roche.com' || domain.endsWith('.roche.com');
    const isGenentech = domain === 'genentech.com' || domain.endsWith('.genentech.com');

    if (!isRoche && !isGenentech) {
      return 'Enter a valid Roche or Genentech email address.';
    }

    return '';
  }

  private isDuplicateEmail(value: string, currentIndex = -1): boolean {
    const normalizedEmail = value.trim().toLowerCase();
    return this.emails.some((entry, index) => index !== currentIndex && entry.value.trim().toLowerCase() === normalizedEmail);
  }

  private validateEmailEdit(index: number): boolean {
    if (!this.validateEmailValue(index)) {
      return false;
    }

    const entry = this.emails[index];
    entry.isEditing = false;
    delete entry.originalValue;
    return true;
  }

  validateEmailValue(index: number): boolean {
    const entry = this.emails[index];
    if (!entry) {
      return false;
    }

    entry.value = entry.value.trim();
    entry.error = this.getEmailValidationError(entry.value);
    if (!entry.error && this.isDuplicateEmail(entry.value, index)) {
      entry.error = 'Duplicate email addresses are not allowed.';
    }

    if (entry.error) {
      return false;
    }

    return true;
  }

  private getApiErrorMessage(error: any): string {
    const res = typeof error === 'function' ? error() : error;
    const errObj = res?.error || res;

    const fieldErrors = errObj?.field_errors
      ?.map((f: any) => f?.message)
      ?.filter(Boolean);

    if (fieldErrors?.length) {
      return fieldErrors.join('. ');
    }

    return 'Unable to save email notification settings. Please try again.';
  }

  private populateForm(data: EmailNotificationSettings): void {
    if (!data) {
      return;
    }

    this.emails = (data.emails || []).filter(email => !!email).slice(0, 3)
      .map(value => ({ value, error: '', isEditing: false }));

    let freq = (data.notificationFrequency || 'off').toLowerCase();
    if (freq === 'immediate') {
      freq = 'immediately';
    }

    this.emailNotificationForm.patchValue({
      notificationType: freq,
      positiveVerification: !!(data.notificationsToMonitor && data.notificationsToMonitor.positive),
      negativeVerification: !!(data.notificationsToMonitor && data.notificationsToMonitor.negative)
    });
  }
}
